/* Lista di risultati: la stessa riga in due forme.
 *
 * Sotto i 720px è una scheda con i tre numeri sulla prima linea; da 720px in
 * su è una riga di tabella. Il componente rende ENTRAMBE le forme e lascia
 * scegliere al CSS: così il primo fotogramma è già giusto, il
 * ridimensionamento non ricarica nulla e la stampa funziona. Decidere in
 * JavaScript significherebbe rimontare l'albero a ogni cambio di larghezza.
 *
 * Nella tabella profitto e drawdown sono adiacenti e non separabili, e le
 * intestazioni NON sono cliccabili: l'ordinamento è una query e vive nel suo
 * controllo dedicato.
 */

import type { ReactNode } from 'react';
import { Distintivo } from './Base';
import { IconaStrategia } from './IconaStrategia';
import { TernaMetriche } from './TernaMetriche';
import { ContenitoreDati, VeloInterrogazione } from './Stati';
import { drawdown as fmtDrawdown, num, pct, timeframe } from '../lib/formato';
import { leggiAPariRischio, tonoRendimento, type Tono } from '../lib/metriche';
import type { StatoDati } from '../api/usaRichiesta';

/** Quel che serve per disegnare una riga, già normalizzato dalla schermata. */
export interface RigaRisultato {
  id: number;
  strategia: string;
  asset: string;
  currency: string;
  /** Dimensione candela in minuti. */
  candleSize: number;
  profitto: number | null;
  drawdown: number | null;
  operazioni: number | null;
  periodo: string;
  /** Distintivo a sinistra, es. il verdetto o "RISCHIOSA". */
  distintivo?: { testo: string; tono: Tono };
  calmar?: number | null;
  sharpe?: number | null;
}

export interface PropsListaRisultati {
  righe: RigaRisultato[];
  stato: StatoDati;
  /** Vincolo che ha svuotato la lista: senza, il vuoto è un enigma. */
  causaVuoto: string;
  azioneVuoto?: { etichetta: string; onClick: () => void };
  messaggioErrore?: string;
  onRiprova?: () => void;
  onApri?: (id: number) => void;
  /** Testo del velo mentre il server rilancia la query. */
  interrogazioneInCorso?: string | null;
  /** Colonna azioni della tabella e piè di scheda su telefono. */
  azioni?: (riga: RigaRisultato) => ReactNode;
}

function coppia(r: RigaRisultato): string {
  return `${r.asset}/${r.currency} · ${timeframe(r.candleSize)}`;
}

function pariRischio(r: RigaRisultato): string {
  return leggiAPariRischio(r.profitto, r.drawdown).valore;
}

export function ListaRisultati({
  righe,
  stato,
  causaVuoto,
  azioneVuoto,
  messaggioErrore,
  onRiprova,
  onApri,
  interrogazioneInCorso,
  azioni,
}: PropsListaRisultati) {
  return (
    <div className="sb-relativo">
      {interrogazioneInCorso && <VeloInterrogazione testo={interrogazioneInCorso} />}

      <ContenitoreDati
        stato={stato}
        causaVuoto={causaVuoto}
        azioneVuoto={azioneVuoto}
        messaggioErrore={messaggioErrore}
        onRiprova={onRiprova}
      >
        {/* --- telefono: schede --- */}
        <div className="sb-lista">
          {righe.map((r) => (
            <SchedaRisultato key={r.id} riga={r} onApri={onApri} azioni={azioni} />
          ))}
        </div>

        {/* --- tablet e desktop: tabella --- */}
        <div className="sb-tabella-guscio">
          <div className="sb-scorri-x">
            <table className="sb-tabella">
              <thead>
                <tr>
                  <th>Strategia</th>
                  <th className="sb-col-profitto">Profitto</th>
                  <th className="sb-col-drawdown">Drawdown</th>
                  <th>Ops</th>
                  <th>Pari rischio</th>
                  <th>Periodo</th>
                  {azioni && <th>Azioni</th>}
                </tr>
              </thead>
              <tbody>
                {righe.map((r) => (
                  <tr
                    key={r.id}
                    onClick={onApri ? () => onApri(r.id) : undefined}
                    style={{ cursor: onApri ? 'pointer' : undefined }}
                  >
                    <td className="sb-cella-testo">
                      <span className="sb-tabella__strategia">
                        <IconaStrategia nome={r.strategia} dim={24} />
                        <span>
                          <span style={{ display: 'block' }}>{r.strategia}</span>
                          <span className="sb-tabella__sotto">{coppia(r)}</span>
                        </span>
                      </span>
                    </td>
                    <td className={`sb-tono-${tonoRendimento(r.profitto)}`}>
                      {r.profitto == null ? 'n/d' : pct(r.profitto)}
                    </td>
                    <td className="sb-tono-perdita">
                      {r.drawdown == null ? 'n/d' : fmtDrawdown(r.drawdown)}
                    </td>
                    <td className="sb-tono-neutro" style={{ fontWeight: 'var(--sb-peso-medio)' }}>
                      {r.operazioni == null ? 'n/d' : num(r.operazioni)}
                    </td>
                    <td className="sb-tono-neutro">{pariRischio(r)}</td>
                    <td
                      className="sb-tono-neutro"
                      style={{ fontWeight: 'var(--sb-peso-medio)', fontSize: 'var(--sb-t-105)', color: 'var(--sb-testo-tenue)' }}
                    >
                      {r.periodo}
                    </td>
                    {azioni && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <span style={{ display: 'inline-flex', gap: 'var(--sb-s-2)' }}>{azioni(r)}</span>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </ContenitoreDati>
    </div>
  );
}

function SchedaRisultato({
  riga: r,
  onApri,
  azioni,
}: {
  riga: RigaRisultato;
  onApri?: (id: number) => void;
  azioni?: (riga: RigaRisultato) => ReactNode;
}) {
  const contenuto = (
    <>
      <div className="sb-scheda-risultato__testata">
        <IconaStrategia nome={r.strategia} dim={22} />
        {r.distintivo && <Distintivo tono={r.distintivo.tono}>{r.distintivo.testo}</Distintivo>}
        <span className="sb-scheda-risultato__nome">{r.strategia}</span>
      </div>

      {/* I tre numeri restano tre anche a 390px: la regola non cambia con la
          larghezza dello schermo. La terna è il componente condiviso e non
          tre riquadri riscritti qui: erano la stessa forma in due copie, e la
          copia non avrebbe seguito il periodo quando è comparso. */}
      <TernaMetriche
        densita="riquadri"
        profitto={r.profitto}
        drawdown={r.drawdown}
        operazioni={r.operazioni}
        periodo={r.periodo}
      />

      <div className="sb-scheda-risultato__coda">
        <span>{coppia(r)}</span>
        <span>
          A pari rischio <b>{pariRischio(r)}</b>
        </span>
        {r.calmar != null && (
          <span>
            Calmar <b>{num(r.calmar, 2)}</b>
          </span>
        )}
        {r.sharpe != null && (
          <span>
            Sharpe <b>{num(r.sharpe, 2)}</b>
          </span>
        )}
      </div>
    </>
  );

  const classi = `sb-scheda-risultato${r.distintivo?.tono === 'cautela' ? ' sb-scheda-risultato--cautela' : ''}`;

  return (
    <div className={classi}>
      {onApri ? (
        <button
          type="button"
          onClick={() => onApri(r.id)}
          style={{ all: 'unset', display: 'block', width: '100%', cursor: 'pointer' }}
        >
          {contenuto}
        </button>
      ) : (
        contenuto
      )}
      {azioni && (
        <div className="sb-riga" style={{ marginTop: 'var(--sb-s-5)', gap: 'var(--sb-s-3)' }}>
          {azioni(r)}
        </div>
      )}
    </div>
  );
}
